/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import get from 'lodash/get';

import { errorWithLevelWarning } from '../../error/error-with-level';
import { fetchWithTimeout } from '../../fetch/fetch';
import CommunicatorNumber from '../number/communicator-number';
import CommunicatorUtility from '../utility/communicator-utility';

/**
 * @import { HeartbeatFetched, HeartbeatInfo } from '../../heartbeat'
 */

// TODO #94 Přidat unit testy pro nové funkcionality spojené s offline módem.
/**
 * Komunikátor push dat zařízení pro Ki-Wi Server.
 */
export default class CommunicatorHeartbeat {
  /**
   * Vytvoří komunikátor zařízení se serverem.
   */
  constructor() {
    this.fetching = false;
  }

  // TODO #626: Refaktorovat na ServerDeviceId.
  /**
   * Informuje server o online stavu a zároveň doručí push data pro zařízení.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení, které chce komunikovat.
   * @param {string} serverUrl URL adresa Ki-Wi Server pro komunikaci.
   * @returns {Promise<HeartbeatFetched | null>} Heartbeat data, pokud se je podařilo získat.
   */
  fetch(deviceId, serverUrl) {
    // TODO #94 Omezení paralelního fetch:
    // - Zvážit umístění této funkcionality.
    // - Napsat unit testy:
    //   - Využít zpoždění fetch. https://github.com/wheresrhys/fetch-mock/issues/75
    //   - Duplikátní fetch vrací null.
    //   - Nezablokuje se při resolve.
    //   - Nezablokuje se při reject.
    /** @type {Promise<HeartbeatFetched | null>} */
    let result;
    if (this.fetching) {
      // TODO Mohlo by reagovat jinak. Např. vrácením probíhajícího požadavku.
      result = Promise.resolve(null);
    } else {
      this.fetching = true;

      result = fetchWithTimeout(
        `${serverUrl}/api/device/${deviceId}/heartbeat`,
        { method: 'POST' }
      )
        // Snížení priority logování pro chyby síťového spojení.
        .catch((error) => {
          throw errorWithLevelWarning(error);
        })
        .then((response) => {
          const fetchEndTimeMillis = Date.now();

          // Snížení priority logování pro chyby ze serveru.
          if (!response.ok) {
            const error = new RangeError(
              `Invalid HTTP status "${response.status}".`
            );
            throw errorWithLevelWarning(error);
          }

          return response.json().then((json) => {
            const heartbeatInfo = CommunicatorHeartbeat.parse(json);
            /** @type {HeartbeatFetched} */
            const heartbeatFetched = {
              fetchEndTimeMillis,
              heartbeatInfo
            };
            return heartbeatFetched;
          });
        })
        .finally(() => {
          this.fetching = false;
        });
    }
    return result;
  }

  /**
   * Zpracuje push data zařízení.
   * @param {{
   *   appVersion?: string,
   *   contentTimestamp: string | '',
   *   currentTimeMillis?: number,
   *   queues?: Record<string, true>,
   * } | undefined} json Odpověď na push data zařízení.
   * @returns {HeartbeatInfo} Push data.
   */
  static parse(json) {
    return {
      // Ki-Wi Server vrací klíč vždy, hodnota je string ve formátu TimestampString nebo "".
      // TODO: Jak se rozliší případná nepřítomnost položky od špatného formátu datumu
      // (např. právě hodnota "" pro prázdný/neodeslaný obsah)?
      contentTimestamp: CommunicatorUtility.validateTimestampStringOrNull(
        get(json, 'contentTimestamp')
      ),
      // Ki-Wi Server sice vrací klíč vždy, ale historicky tato položka nebyla přítomna od začátku,
      // takže je volitelná. Hodnota je počet ms od UNIX epochy podle času Ki-Wi Serveru.
      currentTimeMillis: CommunicatorNumber.fromNumberOrNull(
        get(json, 'currentTimeMillis')
      ),
      // Ki-Wi Server vrací klíč "queue" jen pokud je mapa neprázdná.
      // Klíče jsou typu `string` s názvem fronty a hodnota je typu `boolean`
      // vždy s hodnotou `true`.
      nonEmptyQueues: new Set(Object.keys(get(json, 'queues', {}))),
      // Ki-Wi Server vrací klíč "appVersion" jen pokud je požadovaná verze aplikace řízena.
      version: CommunicatorUtility.parseStringOrNull(get(json, 'appVersion'))
    };
  }
}
